import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Edit2, Trash2, Image, Save, X, Eye, EyeOff, Flame, Drumstick } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";

const AdminMenuPage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category: "traditional",
    spice_level: "",
    has_meat_choice: false,
    lamb_extra_price: "0",
    has_spice_choice: false,
    allergens: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const categories = [
    { id: "forretter", label: "Forretter" },
    { id: "tandoori", label: "Tandoori Retter" },
    { id: "chef_special", label: "Chef Spesial" },
    { id: "traditional", label: "Tradisjonelle Retter" },
    { id: "vegetar", label: "Vegetar" },
    { id: "seafood", label: "Sjømat" },
    { id: "nan", label: "Nan Brød" },
    { id: "tilbehor", label: "Tilbehør" },
    { id: "barn", label: "Barnemeny" },
  ];

  // Categories that allow customer to choose spice level
  const spiceChoiceCategories = ["tandoori", "chef_special", "traditional", "seafood", "vegetar"];

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`${API}/menu`);
      setMenuItems(response.data);
    } catch (error) {
      console.error("Error fetching menu:", error);
      toast.error("Kunne ikke laste meny");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      price: "",
      category: "traditional",
      spice_level: "",
      has_meat_choice: false,
      lamb_extra_price: "0",
      has_spice_choice: false,
      allergens: "",
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingItem(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      price: item.price.toString(),
      category: item.category,
      spice_level: item.spice_level?.toString() || "",
      has_meat_choice: item.has_meat_choice || false,
      lamb_extra_price: (item.lamb_extra_price || 0).toString(),
      has_spice_choice: item.has_spice_choice || false,
      allergens: item.allergens || "",
    });
    setImagePreview(item.image_url ? `${API.replace('/api', '')}${item.image_url}` : null);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("description", formData.description);
      submitData.append("price", parseFloat(formData.price));
      submitData.append("category", formData.category);
      submitData.append("has_meat_choice", formData.has_meat_choice);
      submitData.append("lamb_extra_price", parseFloat(formData.lamb_extra_price) || 0);
      submitData.append("has_spice_choice", formData.has_spice_choice);
      submitData.append("allergens", formData.allergens);
      if (formData.spice_level) {
        submitData.append("spice_level", parseInt(formData.spice_level));
      }
      if (imageFile) {
        submitData.append("image", imageFile);
      }
      if (editingItem) {
        submitData.append("available", editingItem.available);
      }

      if (editingItem) {
        const token = localStorage.getItem("admin_token");
        await axios.put(`${API}/admin/menu/${editingItem.id}`, submitData, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
        });
        toast.success("Rett oppdatert!");
      } else {
        const token = localStorage.getItem("admin_token");
        await axios.post(`${API}/admin/menu`, submitData, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
        });
        toast.success("Ny rett lagt til!");
      }

      resetForm();
      fetchMenu();
    } catch (error) {
      console.error("Error saving menu item:", error);
      toast.error("Kunne ikke lagre rett");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Er du sikker på at du vil slette denne retten?")) return;

    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/menu/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Rett slettet!");
      fetchMenu();
    } catch (error) {
      console.error("Error deleting menu item:", error);
      toast.error("Kunne ikke slette rett");
    }
  };

  const handleToggleAvailability = async (item) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.patch(`${API}/admin/menu/${item.id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(item.available ? "Rett markert som utilgjengelig" : "Rett markert som tilgjengelig");
      fetchMenu();
    } catch (error) {
      console.error("Error toggling availability:", error);
      toast.error("Kunne ikke oppdatere tilgjengelighet");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      {/* Header */}
      <div 
        className="py-12 mandala-pattern"
        style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl sm:text-4xl font-serif-display font-semibold italic"
                style={{ color: '#d4af37' }}
              >
                Menybehandling
              </motion.h1>
              <p className="text-sm mt-1" style={{ color: '#f5e6d3' }}>{menuItems.length} retter i menyen</p>
            </div>
            <Button 
              onClick={() => { resetForm(); setShowForm(true); }}
              className="rounded-full btn-gold"
            >
              <Plus className="h-5 w-5 mr-2" />
              Legg til Rett
            </Button>
          </div>
        </div>
      </div>

      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Legend */}
        <div className="mb-6 p-4 rounded-xl flex flex-wrap gap-6" style={{ background: '#ede1c8', border: '1px solid #b27f47' }}>
          <div className="flex items-center gap-2">
            <Drumstick className="h-5 w-5" style={{ color: '#721111' }} />
            <span className="text-sm" style={{ color: '#2d1810' }}>= Kjøttvalg (Kylling/Lam)</span>
          </div>
          <div className="flex items-center gap-2">
            <Flame className="h-5 w-5" style={{ color: '#ff6b00' }} />
            <span className="text-sm" style={{ color: '#2d1810' }}>= Kryddernivå (kunde velger 1-5)</span>
          </div>
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(45, 24, 16, 0.9)' }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              style={{ border: '3px solid #d4af37' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-serif-display font-semibold" style={{ color: '#2d1810' }}>
                  {editingItem ? "Rediger Rett" : "Legg til Ny Rett"}
                </h2>
                <Button variant="ghost" onClick={resetForm} style={{ color: '#721111' }}>
                  <X className="h-6 w-6" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Image Upload */}
                <div className="text-center">
                  <div 
                    className="relative w-32 h-32 mx-auto rounded-xl overflow-hidden cursor-pointer group"
                    style={{ background: '#ede1c8', border: '2px dashed #b27f47' }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <Image className="h-8 w-8" style={{ color: '#b27f47' }} />
                        <span className="text-xs mt-1" style={{ color: '#8b4513' }}>Last opp</span>
                      </div>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label style={{ color: '#2d1810' }}>Navn *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="indian-input mt-1"
                    />
                  </div>
                  <div>
                    <Label style={{ color: '#2d1810' }}>Pris (NOK) *</Label>
                    <Input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      className="indian-input mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label style={{ color: '#2d1810' }}>Beskrivelse *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={2}
                    className="indian-input mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label style={{ color: '#2d1810' }}>Kategori *</Label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="indian-input w-full mt-1"
                      required
                    >
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label style={{ color: '#2d1810' }}>Allergener</Label>
                    <Input
                      value={formData.allergens}
                      onChange={(e) => setFormData({ ...formData, allergens: e.target.value })}
                      placeholder="F.eks. M, G, N"
                      className="indian-input mt-1"
                    />
                  </div>
                </div>

                {/* Meat Choice Option */}
                <div 
                  className="p-4 rounded-xl"
                  style={{ background: '#f4e7d8', border: '1px solid #b27f47' }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      id="has_meat_choice"
                      checked={formData.has_meat_choice}
                      onChange={(e) => setFormData({ ...formData, has_meat_choice: e.target.checked })}
                      className="w-5 h-5 rounded"
                      style={{ accentColor: '#721111' }}
                    />
                    <Label htmlFor="has_meat_choice" className="cursor-pointer flex items-center gap-2" style={{ color: '#2d1810' }}>
                      <Drumstick className="h-5 w-5" style={{ color: '#721111' }} />
                      Kjøttvalg (Kylling/Lam)
                    </Label>
                  </div>
                  {formData.has_meat_choice && (
                    <div>
                      <Label style={{ color: '#2d1810' }}>Ekstra pris for Lam (NOK)</Label>
                      <Input
                        type="number"
                        value={formData.lamb_extra_price}
                        onChange={(e) => setFormData({ ...formData, lamb_extra_price: e.target.value })}
                        placeholder="40"
                        className="indian-input mt-1 max-w-xs"
                      />
                    </div>
                  )}
                </div>

                {/* Spice Choice Option */}
                {spiceChoiceCategories.includes(formData.category) && (
                  <div 
                    className="p-4 rounded-xl"
                    style={{ background: '#fff5e6', border: '1px solid #ff6b00' }}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="has_spice_choice"
                        checked={formData.has_spice_choice}
                        onChange={(e) => setFormData({ ...formData, has_spice_choice: e.target.checked })}
                        className="w-5 h-5 rounded"
                        style={{ accentColor: '#ff6b00' }}
                      />
                      <Label htmlFor="has_spice_choice" className="cursor-pointer flex items-center gap-2" style={{ color: '#2d1810' }}>
                        <Flame className="h-5 w-5" style={{ color: '#ff6b00' }} />
                        Kryddernivå (kunde velger 1-5 ved bestilling)
                      </Label>
                    </div>
                  </div>
                )}

                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 rounded-full"
                    style={{ background: 'transparent', border: '2px solid #721111', color: '#721111' }}
                  >
                    Avbryt
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 btn-maroon rounded-full"
                  >
                    <Save className="h-5 w-5 mr-2" />
                    {saving ? "Lagrer..." : "Lagre"}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}

        {/* Menu Items Grid by Category */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" style={{ borderColor: '#721111', borderRightColor: 'transparent' }}></div>
          </div>
        ) : (
          categories.map((category) => {
            const items = menuItems.filter((item) => item.category === category.id);
            if (items.length === 0) return null;
            
            return (
              <div key={category.id} className="mb-8">
                <h2 className="text-xl font-serif-display font-semibold mb-4 pb-2" style={{ color: '#2d1810', borderBottom: '2px solid #d4af37' }}>
                  {category.label} ({items.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className={`bg-white rounded-xl p-4 shadow ${!item.available ? 'opacity-50' : ''}`}
                      style={{ border: '1px solid #b27f47' }}
                    >
                      <div className="flex gap-3">
                        {item.image_url ? (
                          <img
                            src={`${API.replace('/api', '')}${item.image_url}`}
                            alt={item.name}
                            className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ background: '#ede1c8' }}>
                            <Image className="h-8 w-8" style={{ color: '#b27f47' }} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <h3 className="font-semibold text-sm truncate" style={{ color: '#2d1810' }}>{item.name}</h3>
                            <span className="font-bold text-sm ml-2" style={{ color: '#721111' }}>kr {item.price},-</span>
                          </div>
                          <p className="text-xs line-clamp-2 mt-1" style={{ color: '#4a3728' }}>{item.description}</p>
                          
                          {/* Icons for meat/spice options */}
                          <div className="flex gap-2 mt-2">
                            {item.has_meat_choice && (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ background: '#fde8e8', color: '#721111' }}>
                                <Drumstick className="h-3 w-3" />
                                +{item.lamb_extra_price} lam
                              </span>
                            )}
                            {item.has_spice_choice && (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ background: '#fff5e6', color: '#ff6b00' }}>
                                <Flame className="h-3 w-3" />
                                1-5
                              </span>
                            )}
                            {item.allergens && (
                              <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: '#e5e7eb', color: '#374151' }}>
                                {item.allergens}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" onClick={() => handleEdit(item)} className="flex-1 rounded-full text-xs" style={{ background: '#d4af37', color: '#2d1810' }}>
                          <Edit2 className="h-3 w-3 mr-1" />Rediger
                        </Button>
                        <Button size="sm" onClick={() => handleToggleAvailability(item)} className="rounded-full" style={{ background: 'transparent', border: '1px solid #721111', color: '#721111' }}>
                          {item.available ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </Button>
                        <Button size="sm" onClick={() => handleDelete(item.id)} className="rounded-full" style={{ background: '#ef4444', color: 'white' }}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AdminMenuPage;
